/**
 * positionRows — ONE normaliser that turns the app's THREE option-position shapes
 * (manual/Architect baskets, staged-campaign tranches, and auto-engine cycles) into a
 * single common row so the unified "All positions" ledger can show everything in one
 * table with consistent P&L / risk / ROI (the display-side of audit B1).
 *
 * P&L convention (₹): OPEN → live/unrealised; CLOSED → realised. Risk = the position's
 * defined max loss (basket maxLoss / engine cycle invested margin).
 */
import {
  Cycle, structureName, cyclePnlInr, cycleRoiPct,
} from './cycleFormat';

export interface BasketLite {
  id: number; strategy: string; status: string; tradingMode?: 'PAPER' | 'LIVE';
  legs?: Array<{ action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; quantity: number; symbol: string; expiry?: string }>;
  legFills?: Array<{ symbol?: string }>;
  maxProfit?: number | null; maxLoss?: number | null; marginEstimate?: number | null;
  realizedPnl?: number | null; closedAt?: string | null; campaignId?: number | null;
  desynced?: boolean; adjustmentNeeded?: boolean;
  exitMode?: 'FIXED' | 'PREMIUM' | null; slAmountInr?: number | null; adjustmentsRealizedPnl?: number | null;
  live?: { unrealizedPnl?: number; nearExpiryMs?: number | null } | null;
}

export type PositionOrigin = 'basket' | 'campaign' | 'engine';
export interface PositionRow {
  key: string;
  origin: PositionOrigin;
  originLabel: string;      // e.g. "Basket #112", "Campaign #14", or the engine instance name
  name: string;             // strategy / structure headline
  symbol: string;           // underlying (BTC / NIFTY / …)
  structure: string;        // compact leg summary
  mode: 'PAPER' | 'LIVE';
  status: string;           // OPEN / CLOSED / PENDING …
  isOpen: boolean;
  pnl: number | null;       // ₹ (live for open, realised for closed)
  roi: number | null;       // % on risk
  maxProfit: number | null; // ₹
  risk: number | null;      // ₹ defined max loss / margin
  expiryMs: number | null;  // nearest leg settlement (for display + sort)
  flag: 'desync' | 'attention' | 'thin' | null;
  manage: 'baskets' | 'engines';   // which detail surface manages this row
}

/** Underlying symbol — Delta legs are {C|P}-BASE-STRIKE-DDMMYY; NSE legs start with the
 *  underlying name then digits. Mirrors OptionBasketsMonitor's underlyingOf. */
const underlyingOfBasket = (b: BasketLite): string => {
  const sym = String(b.legFills?.[0]?.symbol || b.legs?.[0]?.symbol || '').toUpperCase();
  const delta = /^[CP]-([A-Z0-9]+)-/.exec(sym); if (delta) return delta[1];
  const nse = /^([A-Z&]+?)\d/.exec(sym); if (nse) return nse[1];
  return sym || '—';
};

const basketStructure = (b: BasketLite): string => {
  const legs = b.legs || [];
  if (!legs.length) return b.strategy || 'basket';
  const s = legs.filter(l => l.action === 'SELL').length;
  const l = legs.filter(l => l.action === 'BUY').length;
  const types = Array.from(new Set(legs.map(x => x.optionType))).join('+');
  return `${legs.length} legs · ${s}S/${l}B · ${types}`;
};

/** Delta settles 12:00 UTC — good enough for a display/sort timestamp from a YYYY-MM-DD. */
const expiryStrToMs = (d?: string | null): number | null => {
  if (!d) return null;
  const t = Date.parse(`${d.slice(0, 10)}T12:00:00Z`);
  return Number.isFinite(t) ? t : null;
};

export function basketRows(baskets: BasketLite[]): PositionRow[] {
  const rows: PositionRow[] = [];
  for (const b of baskets) {
    const isOpen = b.status === 'EXECUTED' && !b.closedAt;
    // Skip not-yet-placed baskets (pending approval / trigger) — the ledger is placed positions.
    if (!isOpen && b.status !== 'EXECUTED') continue;
    const origin: PositionOrigin = b.campaignId != null ? 'campaign' : 'basket';
    // Open P&L includes what adjustments already booked (the basket's total), matching the
    // basket card; closed realizedPnl already includes them.
    const pnl = isOpen
      ? (b.live?.unrealizedPnl != null ? Math.round(Number(b.live.unrealizedPnl) + Number(b.adjustmentsRealizedPnl || 0)) : null)
      : (b.realizedPnl != null ? Math.round(Number(b.realizedPnl)) : null);
    // A fixed-₹ basket's risk is its stop, not the structure's theoretical max loss.
    const risk = b.exitMode === 'FIXED' && Number(b.slAmountInr) !== 0 && Number.isFinite(Number(b.slAmountInr)) ? Math.max(0, Number(b.slAmountInr))
      : b.maxLoss != null ? Math.abs(Number(b.maxLoss)) : (b.marginEstimate != null ? Number(b.marginEstimate) : null);
    const roi = pnl != null && risk && risk > 0 ? (pnl / risk) * 100 : null;
    rows.push({
      key: `b${b.id}`, origin,
      originLabel: origin === 'campaign' ? `Campaign #${b.campaignId}` : `Basket #${b.id}`,
      name: b.strategy || 'Basket', symbol: underlyingOfBasket(b), structure: basketStructure(b),
      mode: b.tradingMode || 'PAPER',
      status: isOpen ? 'OPEN' : 'CLOSED', isOpen,
      pnl, roi, maxProfit: b.maxProfit != null ? Math.abs(Number(b.maxProfit)) : null, risk,
      expiryMs: b.live?.nearExpiryMs ?? null,
      flag: b.desynced ? 'desync' : (b.adjustmentNeeded ? 'attention' : null),
      manage: 'baskets',
    });
  }
  return rows;
}

export function engineRows(instances: any[]): PositionRow[] {
  const rows: PositionRow[] = [];
  for (const inst of instances || []) {
    if (!inst.multiLeg) continue; // single-leg engines use the positions model — out of scope for the ledger
    for (const c of (inst.cycles || []) as Cycle[]) {
      const isOpen = c.status === 'OPEN';
      const optType = c.legs?.[0]?.optType;
      const name = structureName(c.meta?.kind, c.meta?.avSide, optType);
      const strikes = c.meta?.shortStrike != null && c.meta?.longStrike != null ? `${c.meta.shortStrike}/${c.meta.longStrike}` : '';
      const rr = c.meta?.rr;
      rows.push({
        key: `c${c.id}`, origin: 'engine', originLabel: inst.name || inst.engine,
        name, symbol: c.symbol, structure: `${name} ${strikes}`.trim(),
        mode: (c.mode as 'PAPER' | 'LIVE') || 'PAPER',
        status: isOpen ? 'OPEN' : 'CLOSED', isOpen,
        pnl: cyclePnlInr(c), roi: cycleRoiPct(c),
        maxProfit: c.maxProfitInr ?? null, risk: c.investedInr ?? null,
        expiryMs: expiryStrToMs(c.meta?.expiry),
        flag: rr != null && rr < 0.5 ? 'thin' : null,
        manage: 'engines',
      });
    }
  }
  return rows;
}

/** Open positions first, then most-recent; within each, biggest risk first. */
export function orderRows(rows: PositionRow[]): PositionRow[] {
  return rows.slice().sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    return (b.risk || 0) - (a.risk || 0);
  });
}

export function rowTotals(rows: PositionRow[]) {
  const open = rows.filter(r => r.isOpen);
  const livePnl = open.reduce((s, r) => s + (r.pnl || 0), 0);
  const risk = open.reduce((s, r) => s + (r.risk || 0), 0);
  return { openCount: open.length, total: rows.length, livePnl: Math.round(livePnl), risk: Math.round(risk) };
}
